import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/brand/SeoHead";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import GradientBorderCard from "@/components/brand/GradientBorderCard";
import FadeUp from "@/components/brand/FadeUp";
import { FloatingAccents, AccentDot } from "@/components/brand/AccentShapes";
import ProgramSnapshot from "@/components/sections/ProgramSnapshot";
import FounderNote from "@/components/sections/FounderNote";
import EarlyStageNote from "@/components/sections/EarlyStageNote";
import heroImg from "@/assets/brand/hero_home.png";
import whatStudentsBuildImg from "@/assets/brand/what_students_build.png";
import fblCardImg from "@/assets/brand/fbl_card.png";
import sponsorImg from "@/assets/brand/sponsor_hero.png";

const BULLETS = [
    { text: "AI tools are becoming part of many careers.", color: "#EC4899" },
    {
        text: "Students need safe, guided ways to learn these tools.",
        color: "#8B5CF6",
    },
    {
        text: "Creative students deserve practical pathways too.",
        color: "#3B82F6",
    },
    {
        text: "Confidence grows when students finish something real.",
        color: "#14B8A6",
    },
];

/* Small inline SVG icons for the "What students build" cards */
const IconBrowser = () => (
    <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
    >
        <rect
            x="3"
            y="6"
            width="26"
            height="20"
            rx="3"
            stroke="#EC4899"
            strokeWidth="1.8"
        />
        <line
            x1="3"
            y1="12"
            x2="29"
            y2="12"
            stroke="#EC4899"
            strokeWidth="1.8"
        />
        <circle cx="6.5" cy="9" r="1" fill="#EC4899" />
        <circle cx="9.5" cy="9" r="1" fill="#EC4899" />
        <circle cx="12.5" cy="9" r="1" fill="#EC4899" />
        <line
            x1="8"
            y1="18"
            x2="24"
            y2="18"
            stroke="#EC4899"
            strokeOpacity="0.6"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
        <line
            x1="8"
            y1="22"
            x2="20"
            y2="22"
            stroke="#EC4899"
            strokeOpacity="0.6"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);
const IconSparkle = () => (
    <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M16 4 L18.5 13.5 L28 16 L18.5 18.5 L16 28 L13.5 18.5 L4 16 L13.5 13.5 Z"
            fill="#8B5CF6"
            fillOpacity="0.18"
            stroke="#8B5CF6"
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        <circle cx="26" cy="7" r="1.5" fill="#FBBF24" />
        <circle cx="6" cy="25" r="1.2" fill="#14B8A6" />
    </svg>
);
const IconCheckStar = () => (
    <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M16 3 L19 12 L28 12 L20.8 18 L23.5 27 L16 21.5 L8.5 27 L11.2 18 L4 12 L13 12 Z"
            fill="#FBBF24"
            fillOpacity="0.2"
            stroke="#FBBF24"
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        <path
            d="M11 16 L14.5 19.5 L21 13"
            stroke="#3B82F6"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const BUILD_CARDS = [
    {
        title: "Personal Website",
        body: "A simple portfolio site students can use to introduce themselves, their interests, and their creative work.",
        icon: <IconBrowser />,
        accent: "#EC4899",
    },
    {
        title: "AI Prompting Basics",
        body: "Students learn how to use AI as a helpful tool without copying blindly or sharing unsafe information.",
        icon: <IconSparkle />,
        accent: "#8B5CF6",
    },
    {
        title: "Digital Confidence",
        body: "Students practice presenting their ideas, making revisions, and finishing a real project.",
        icon: <IconCheckStar />,
        accent: "#FBBF24",
    },
];

export default function Home() {
    return (
        <>
            <SeoHead
                title="Vibe for Success | AI-Era Workforce Readiness for Lee County Students"
                description="Vibe for Success, Inc. is a Florida nonprofit corporation launching Future Builders Lab — a 6-week creative workforce-readiness pilot for Lee County high school students."
                path="/"
                image="/hero_home.png"
            />

            {/* ================= SECTION 1 — HERO ================= */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="home-hero"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
            >
                <GlowingGridBackground accent="#8B5CF6" opacity={0.18} />
                <div className="vfs-container relative">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <FadeUp>
                            <span
                                className="vfs-eyebrow"
                                data-testid="hero-eyebrow"
                            >
                                <AccentDot size={6} color="#EC4899" />
                                Florida Nonprofit Corporation — First Pilot in
                                Development
                            </span>
                            <h1
                                className="vfs-h1 mt-5"
                                data-testid="hero-h1"
                            >
                                Helping Lee County students build{" "}
                                <span className="vfs-grad-text">
                                    confidence, digital skills,
                                </span>{" "}
                                and real portfolio websites for the AI-era
                                workforce.
                            </h1>
                            <p
                                className="mt-6 text-lg leading-relaxed max-w-xl"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Vibe for Success, Inc. is a Florida nonprofit
                                corporation launching Future Builders Lab, a
                                6-week creative workforce-readiness pilot for
                                high school students in Southwest Florida.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <Link
                                    to="/sponsor"
                                    className="vfs-cta"
                                    data-testid="hero-primary-cta"
                                >
                                    Sponsor a Student
                                </Link>
                                <Link
                                    to="/student-interest"
                                    className="vfs-cta-outline"
                                    data-testid="hero-secondary-cta"
                                >
                                    Student / Parent Interest
                                </Link>
                            </div>
                            <p
                                className="mt-6 text-sm italic max-w-xl"
                                style={{ color: "var(--text-secondary)" }}
                                data-testid="hero-trust-note"
                            >
                                First pilot in development for Lee County
                                students, with North Fort Myers and Cape Coral
                                as the initial focus area.
                            </p>
                        </FadeUp>

                        <FadeUp delay={0.1}>
                            <div className="relative">
                                <FloatingAccents />
                                <img
                                    src={heroImg}
                                    alt="Abstract illustration of overlapping portfolio website cards and AI prompt blocks representing the Future Builders Lab program."
                                    loading="eager"
                                    fetchPriority="high"
                                    width="900"
                                    height="700"
                                    className="relative w-full h-auto rounded-3xl"
                                    style={{
                                        boxShadow:
                                            "0 30px 80px rgba(139, 92, 246, 0.25)",
                                    }}
                                    data-testid="hero-image"
                                />
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ================= SECTION 2 — WHAT STUDENTS BUILD ================= */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="home-build"
            >
                <div className="vfs-container relative">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">
                                Every student leaves with something real.
                            </h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Future Builders Lab is not a lecture-only
                                program. Students use AI-assisted web tools,
                                beginner-friendly design practices, light
                                HTML/CSS, and digital safety guidance to create
                                a safe, parent-approved personal portfolio
                                website.
                            </p>
                        </FadeUp>
                    </div>

                    <FadeUp delay={0.1}>
                        <div className="mt-10">
                            <img
                                src={whatStudentsBuildImg}
                                alt="Three glowing browser window mockups representing personal portfolio websites built by students."
                                loading="lazy"
                                width="1400"
                                height="600"
                                className="w-full h-auto rounded-2xl"
                                style={{
                                    boxShadow:
                                        "0 20px 60px rgba(20, 184, 166, 0.18)",
                                }}
                            />
                        </div>
                    </FadeUp>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {BUILD_CARDS.map((c, i) => (
                            <FadeUp key={c.title} delay={i * 0.08}>
                                <GlassCard
                                    accentColor={c.accent}
                                    className="p-7 h-full"
                                >
                                    <div
                                        className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl"
                                        style={{
                                            background: `${c.accent}18`,
                                            border: `1px solid ${c.accent}45`,
                                        }}
                                    >
                                        {c.icon}
                                    </div>
                                    <h3 className="vfs-h3 mb-3">{c.title}</h3>
                                    <p
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        {c.body}
                                    </p>
                                </GlassCard>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ================= SECTION 3 — WHY IT MATTERS ================= */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="home-why"
            >
                <GlowingGridBackground accent="#3B82F6" opacity={0.1} />
                <div className="vfs-container relative">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                        <div className="lg:col-span-5">
                            <FadeUp>
                                <h2 className="vfs-h2">
                                    The future of work is{" "}
                                    <span className="vfs-grad-text">
                                        already changing.
                                    </span>
                                </h2>
                                <p
                                    className="mt-5 text-lg leading-relaxed"
                                    style={{ color: "var(--text-secondary)" }}
                                >
                                    Students do not need to become professional
                                    coders to benefit from digital skills. They
                                    need confidence, judgment, creativity,
                                    communication skills, and practical
                                    experience using modern tools responsibly.
                                </p>
                            </FadeUp>
                        </div>
                        <div className="lg:col-span-7">
                            <FadeUp delay={0.1}>
                                <ul className="space-y-4">
                                    {BULLETS.map((b, i) => (
                                        <li
                                            key={i}
                                            className="flex items-start gap-4 vfs-glass p-5"
                                            style={{ borderRadius: 16 }}
                                        >
                                            <span
                                                className="mt-1 flex-shrink-0 inline-block w-3 h-3 rounded-full"
                                                style={{
                                                    background: b.color,
                                                    boxShadow: `0 0 14px ${b.color}90`,
                                                }}
                                                aria-hidden="true"
                                            />
                                            <span
                                                className="text-base sm:text-lg"
                                                style={{
                                                    color: "var(--text-primary)",
                                                }}
                                            >
                                                {b.text}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </FadeUp>
                        </div>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ================= SECTION 4 — FBL OVERVIEW ================= */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="home-fbl-overview"
            >
                <div className="vfs-container relative">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <span className="vfs-eyebrow">
                                A Vibe for Success Program
                            </span>
                            <h2 className="vfs-h2 mt-5">
                                Future Builders Lab
                            </h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Future Builders Lab is a 6-week creative
                                workforce-readiness program for high school
                                students in Lee County, Florida. The first
                                pilot is designed for a small group of 5–8
                                students and meets once per week for 2 hours.
                            </p>
                        </FadeUp>
                    </div>

                    <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                        <ProgramSnapshot />
                        <FadeUp delay={0.12}>
                            <img
                                src={fblCardImg}
                                alt="Featured Future Builders Lab program card illustration."
                                loading="lazy"
                                width="900"
                                height="700"
                                className="w-full h-auto rounded-3xl"
                                style={{
                                    boxShadow:
                                        "0 22px 64px rgba(236, 72, 153, 0.22)",
                                }}
                            />
                        </FadeUp>
                    </div>

                    <FadeUp>
                        <div className="mt-10">
                            <Link
                                to="/future-builders-lab"
                                className="vfs-cta"
                                data-testid="home-fbl-view-cta"
                            >
                                View the Program
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ================= SECTION 5 — SPONSOR ================= */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="home-sponsor"
            >
                <GlowingGridBackground accent="#EC4899" opacity={0.1} />
                <div className="vfs-container relative">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <FadeUp>
                            <img
                                src={sponsorImg}
                                alt="Illustration representing a sponsored student seat in the Future Builders Lab pilot."
                                loading="lazy"
                                width="900"
                                height="700"
                                className="w-full h-auto rounded-3xl"
                                style={{
                                    boxShadow:
                                        "0 22px 64px rgba(251, 191, 36, 0.18)",
                                }}
                            />
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <h2 className="vfs-h2">
                                Sponsor one student seat for{" "}
                                <span className="vfs-grad-text">$500.</span>
                            </h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Local businesses can help make the first Future
                                Builders Lab pilot free for students by
                                sponsoring program seats. Each $500 sponsorship
                                helps cover one student&apos;s participation in the
                                6-week pilot.
                            </p>

                            <div
                                className="mt-8 p-5 sm:p-6 rounded-2xl"
                                style={{
                                    background: "rgba(251, 191, 36, 0.06)",
                                    border: "1px solid rgba(251, 191, 36, 0.28)",
                                }}
                                data-testid="sponsor-disclaimer"
                            >
                                <p
                                    className="text-sm leading-relaxed"
                                    style={{ color: "var(--text-primary)" }}
                                >
                                    Vibe for Success, Inc. is a Florida
                                    nonprofit corporation. Sponsorships and
                                    program support help fund pilot program
                                    development and student participation.
                                    Tax-deductible donation status should not
                                    be assumed unless confirmed in writing by
                                    Vibe for Success, Inc.
                                </p>
                            </div>

                            <div className="mt-8">
                                <Link
                                    to="/sponsor"
                                    className="vfs-cta"
                                    data-testid="home-sponsor-cta"
                                >
                                    Become a Sponsor
                                </Link>
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ================= SECTION 6 — PARENT/STUDENT INTEREST ================= */}
            <section
                className="vfs-section relative"
                data-testid="home-interest"
            >
                <div className="vfs-container">
                    <FadeUp>
                        <div className="max-w-3xl">
                            <h2 className="vfs-h2">
                                Interested in joining the first pilot?
                            </h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Students and parents can submit an interest
                                form to learn more about the first Future
                                Builders Lab pilot. Parent or guardian approval
                                is required before a student participates.
                            </p>
                            <div className="mt-8">
                                <Link
                                    to="/student-interest"
                                    className="vfs-cta"
                                    data-testid="home-interest-cta"
                                >
                                    Submit Student Interest Form
                                </Link>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ================= SECTION 7 — PARTNER ================= */}
            <section
                className="vfs-section relative"
                data-testid="home-partner"
            >
                <div className="vfs-container">
                    <FadeUp>
                        <div className="max-w-3xl">
                            <h2 className="vfs-h2">
                                Community partners can help bring the lab to
                                life.
                            </h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Vibe for Success is looking to connect with
                                community spaces, youth organizations,
                                libraries, schools, and local partners
                                interested in supporting safe, creative digital
                                skills training for students.
                            </p>
                            <div className="mt-8">
                                <Link
                                    to="/partner"
                                    className="vfs-cta"
                                    data-testid="home-partner-cta"
                                >
                                    Partner With Us
                                </Link>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* ================= SECTION 8 — FOUNDER NOTE ================= */}
            <FounderNote />

            {/* ================= SECTION 9 — EARLY STAGE ================= */}
            <EarlyStageNote />
        </>
    );
}
